import { SafeHtml } from '@angular/platform-browser';
import { IBusinessConfigFlags } from '@g99-product-web-ui/shared-interface-lib';
import {
  HELP_DOCS,
  LOCAL_STORAGE_KEYS,
  ROUTE_CONSTS,
} from '@g99-product-web-ui/shared-util-lib';

import { IMenuItem, ISubMenuItem } from '../../interfaces/IMenu.interface';

/** Menu id of the Appointments module; gated by the appointment feature flag. */
const APPOINTMENTS_MENU_ID = 'appointments';

// Header routing menus
export function getNavMenuItems(): IMenuItem[] {
  const currentUserString = localStorage.getItem(
    LOCAL_STORAGE_KEYS.CURRENT_USER
  );
  const currentUser = currentUserString ? JSON.parse(currentUserString) : null;
  const isSupportUser = currentUser?.supportUser === true;

  // When Gia is active and AI Campaign is enabled, the Automation "Trigger"
  // entry becomes "AI Campaign" and points at the AI Campaign listing instead.
  const aiCampaignActive = shouldShowAICampaign();

  const menuItems: IMenuItem[] = [
    {
      label: 'Inbox',
      route: ROUTE_CONSTS.INBOX.base,
      id: 'inbox',
      submenu: null,
    },
    {
      label: 'Tasks',
      route: ROUTE_CONSTS.TASK.base,
      id: 'tasks',
      submenu: null,
    },
    {
      label: 'Contacts',
      route: ROUTE_CONSTS.CONTACTS.base,
      id: 'contacts',
      submenu: null,
    },
    {
      label: 'Appointments',
      route: ROUTE_CONSTS.APPOINTMENTS.base,
      id: APPOINTMENTS_MENU_ID,
      submenu: null,
    },
    {
      label: 'Marketing',
      route: '',
      id: 'marketing',
      submenu: [
        {
          label: 'Campaigns',
          route: ROUTE_CONSTS.CAMPAIGN.base,
          id: 'campaigns',
        },
        {
          label: 'Templates',
          route: ROUTE_CONSTS.TEMPLATES.base,
          id: 'templates',
        },
        {
          label: 'Landing Pages',
          route: ROUTE_CONSTS.LANDINGPAGE.base,
          id: 'landing-pages',
        },
        { label: 'Forms', route: ROUTE_CONSTS.FORM_BUILDER.base, id: 'forms' },
        {
          label: 'Review Manager',
          route: ROUTE_CONSTS.REVIEWS.home,
          id: 'review-manager',
        },
        {
          label: 'Cliffhanger',
          route: '',
          id: 'cliffhanger',
          submenu: getCliffhangerSubmenu(),
        },
      ],
      isActive: false,
    },
    {
      label: 'Content',
      route: '',
      id: 'content',
      submenu: [
        { label: 'Files', route: ROUTE_CONSTS.FILE_MANAGER.base, id: 'files' },
        {
          label: 'Social Media Manager',
          route: ROUTE_CONSTS.SOCIAL_MEDIA.base,
          id: 'social-media-manager',
        },
        {
          label: 'AI Draft Blogs',
          route: 'social-media/ai-draft-blogs',
          id: 'ai-draft-blogs',
        },
      ],
      isActive: false,
    },
    {
      label: 'Automations',
      route: '',
      id: 'automations',
      submenu: [
        {
          label: aiCampaignActive ? 'AI Campaign' : 'Trigger',
          route: aiCampaignActive
            ? ROUTE_CONSTS.AI_CAMPAIGN.base
            : ROUTE_CONSTS.AUTOMATIONS.base,
          id: 'Triggers',
        },
        {
          label: 'Lead Status Automation',
          route: ROUTE_CONSTS.LEAD_STATUS_AUTOMATION.base,
          id: 'LeadStatusAutomation',
        },
      ],
    },
    {
      label: 'Reports',
      route: ROUTE_CONSTS.REPORTS.base,
      id: 'reports',
      submenu: null,
    },
  ];

  // Conditionally add AI Hub
  if (isSupportUser) {
    menuItems.push({
      label: 'AI Hub',
      route: ROUTE_CONSTS.AI_AGENTS.base,
      id: 'ai-services',
    });
  }

  // Hide the Appointments module when appointment is disabled for the business
  return menuItems.filter(
    (item) => item.id !== APPOINTMENTS_MENU_ID || shouldShowAppointments()
  );
}

/**
 * Check if the Appointments module should be shown based on the business config flag
 */
function shouldShowAppointments(): boolean {
  const businessConfigString = localStorage.getItem(
    LOCAL_STORAGE_KEYS.BUSINESS_CONFIG
  );
  const businessConfig: IBusinessConfigFlags | null = businessConfigString
    ? JSON.parse(businessConfigString)
    : null;

  return businessConfig?.isAppointmentEnabled === true;
}

/**
 * Whether the Automation tab should surface AI Campaign in place of Trigger.
 * Only when Gia is active for the business AND the AI Campaign flag is enabled.
 */
function shouldShowAICampaign(): boolean {
  const businessConfigString = localStorage.getItem(
    LOCAL_STORAGE_KEYS.BUSINESS_CONFIG
  );
  const businessConfig: IBusinessConfigFlags | null = businessConfigString
    ? JSON.parse(businessConfigString)
    : null;

  return (
    businessConfig?.isAiResponderEnabled === true &&
    businessConfig?.isAICampaignEnabled === true
  );
}

/**
 * Check if Self-Assessment should be shown based on business specialization
 */
function shouldShowSelfAssessment(): boolean {
  const businessInfoString = localStorage.getItem(
    LOCAL_STORAGE_KEYS.BUSINESS_INFO
  );
  const businessInfo = businessInfoString
    ? JSON.parse(businessInfoString)
    : null;

  return (
    businessInfo?.aestheticSpecialization === true ||
    businessInfo?.dentalSpecializationOnly === true
  );
}

/**
 * Build Cliffhanger submenu with conditional Self-Assessment item
 */
function getCliffhangerSubmenu(): ISubMenuItem[] {
  const cliffhangerSubmenu: ISubMenuItem[] = [];

  // Conditionally add Self-Assessment
  if (shouldShowSelfAssessment()) {
    cliffhangerSubmenu.push({
      label: 'Self-Assessment',
      route: ROUTE_CONSTS.SelfAssessment.base,
      id: 'self-assessment',
    });
  }

  // Always include Chatbot and Configuration
  cliffhangerSubmenu.push(
    {
      label: 'Chat Setup',
      route: ROUTE_CONSTS.CHATBOT.chatbot,
      id: 'chatbot',
    },
    {
      label: 'Cliffhanger Configuration',
      route: ROUTE_CONSTS.CLIFFHANGER.base,
      id: 'cliffhanger-config',
    }
  );

  return cliffhangerSubmenu;
}

export const getProfileMenus = (userId: number) => {
  const menus = [
    {
      label: 'Business Settings',
      route: `${ROUTE_CONSTS.BUSINESS.base}/${ROUTE_CONSTS.BUSINESS.businessProfile}`,
      icon: '' as string | SafeHtml,
    },
    {
      label: 'My Profile',
      route: `${ROUTE_CONSTS.USERS.base}/user-detail/${userId}/${ROUTE_CONSTS.USERS.edit}`,
      icon: '' as string | SafeHtml,
    },
    {
      label: 'Third-Party Integrations',
      route: `${ROUTE_CONSTS.THIRDPARTYINTEGRATION.base}`,
      icon: '' as string | SafeHtml,
    },
    {
      label: 'API Docs',
      route: `${ROUTE_CONSTS.API_INTEGRATION.base}/${ROUTE_CONSTS.API_INTEGRATION.apiKeys}`,
      icon: '' as string | SafeHtml,
    },
    {
      label: 'Help Center',
      route: HELP_DOCS.HELP_CENTER,
      icon: '' as string | SafeHtml,
    },
    {
      label: 'Refer a Practice',
      route: HELP_DOCS.REFER_A_PRACTICE,
      icon: '' as string | SafeHtml,
    },
    {
      label: 'Support Tickets',
      route: HELP_DOCS.SUPPORT_TICKETS,
      icon: '' as string | SafeHtml,
    },
    {
      label: 'Sign Out',
      route: '/',
      icon: '' as string | SafeHtml,
    },
  ];

  return menus;
};

// Profile popover routing menus

// Enums for Profile Popover Labels
export enum ProfileMenuLabel {
  BUSINESS_SETTINGS = 'Business Settings',
  USERS = 'Users',
  MY_PROFILE = 'My Profile',
  CHANGE_PASSWORD = 'Change Password',
  USERS_AND_PERMISSIONS = 'Users & Permissions',
  THIRD_PARTY_INTEGRATIONS = 'Third-Party Integrations',
  API_DOCS = 'API Docs',
  REPORTS = 'Reports',
  REFER_A_PRACTICE = 'Refer a Practice',
  SUPPORT_TICKETS = 'Support Tickets',
  HELP_CENTER = 'Help Center',
  SIGN_OUT = 'Sign Out',
}
